import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { EditorState, convertFromRaw } from "draft-js";
import moment from "moment";
import { libraryService } from "../../service/question/libraryService";
import { createService } from "../../service/question/createService";
import {
  questionTypeConstants,
  questionMCQAnswerTypeConstants,
  customerConstants,
} from "../../helper/constants";
import config from "../../helper/config";
import { useQuestionMeta } from "./QuestionMetaContext";

const ViewQuestionContext = createContext(null);

const getCustomerFeatureFlag = () => {
  return config.customerConfigs.featureFlag;
};

const customer = getCustomerFeatureFlag();
const gradeText =
  customer === customerConstants.EXO_CUSTOMER2 ? "Year" : "Grade";

const safeParseDraftArray = (value) => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const toEditorState = (rawDraft) => {
  if (!rawDraft) return null;

  try {
    return EditorState.createWithContent(convertFromRaw(rawDraft));
  } catch {
    return null;
  }
};

const normalizeQuestionResponse = (response) => {
  const data = response?.data ?? response?.question ?? response;

  if (Array.isArray(data)) return data[0] || null;

  if (Array.isArray(data?.question)) return data.question[0] || null;

  if (Array.isArray(data?.data)) return data.data[0] || null;

  return data || null;
};

export function ViewQuestionProvider({ children }) {
  const meta = useQuestionMeta();

  const [question, setQuestion] = useState(null);

  const [questionEditorState, setQuestionEditorState] = useState(null);
  const [passageEditorState, setPassageEditorState] = useState(null);
  const [descriptiveAnswerState, setDescriptiveAnswerState] = useState(null);

  const [mcqAnswerStates, setMcqAnswerStates] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState([]);

  const [attachments, setAttachments] = useState([]);

  const [subQuestionContentStates, setSubQuestionContentStates] = useState([]);
  const [subQuestionAnswerStates, setSubQuestionAnswerStates] = useState([]);

  const [loading, setLoading] = useState(false);
  const [subQuestionLoading, setSubQuestionLoading] = useState(false);
  const [error, setError] = useState("");

  const resetViewState = useCallback(() => {
    setQuestion(null);
    setQuestionEditorState(null);
    setPassageEditorState(null);
    setDescriptiveAnswerState(null);
    setMcqAnswerStates([]);
    setSelectedChoices([]);
    setAttachments([]);
    setSubQuestionContentStates([]);
    setSubQuestionAnswerStates([]);
    setError("");
  }, []);

  const getQuestionTypeName = useCallback((qType, isSubquestion) => {
    if (qType === questionTypeConstants.MCQ.id) {
      return questionTypeConstants.MCQ.name;
    }

    if (qType === questionTypeConstants.DESCRIPTIVE.id) {
      return questionTypeConstants.DESCRIPTIVE.name;
    }

    if (qType === questionTypeConstants.SUBMISSION.id) {
      return questionTypeConstants.SUBMISSION.name;
    }

    if (
      qType === questionTypeConstants.SUBQUESTION.id &&
      isSubquestion === "true"
    ) {
      return questionTypeConstants.SUBQUESTION.name;
    }

    if (
      qType === questionTypeConstants.SUBQUESTION.id &&
      isSubquestion === null
    ) {
      return `${questionTypeConstants.SUBQUESTION.name} (Main-Question)`;
    }

    return "N/A";
  }, []);

  const getCreatedAtLocal = useCallback((time) => {
    if (!time) return "N/A";
    return moment(new Date(`${time}Z`)).local().format("MMMM Do YYYY hh:mm A");
  }, []);

  const getPassageEditorState = useCallback(
    (passageId) => {
      if (!passageId) return null;

      const passage = meta.meta.passages?.find(
        (item) => String(item.passage_id) === String(passageId)
      );

      if (!passage?.content) return null;

      const parsedContent = safeParseDraftArray(passage.content);

      if (
        parsedContent[0]?.blocks &&
        parsedContent[0]?.entityMap !== undefined
      ) {
        return toEditorState(parsedContent[0]);
      }

      return null;
    },
    [meta.meta.passages]
  );

    const loadQuestionForView = useCallback(
        async (questionId) => {
            resetViewState();
            setLoading(true);

        try {
          if (!meta.rawMetaData?.length) {
            await meta.loadQuestionMetaData();
          }
          const response = await createService.getQuestionById(questionId);
          const selectedQuestion = normalizeQuestionResponse(response);

          if (!selectedQuestion) {
                    throw new Error("Question not found.");
                }

                setQuestion(selectedQuestion);

                const questionContent = await libraryService.getQuestionContent(
                    selectedQuestion
                );

                const questionStates = safeParseDraftArray(questionContent);
                setQuestionEditorState(toEditorState(questionStates[0]));

                if (
                    selectedQuestion?.is_passage === true &&
                    selectedQuestion?.passage_id !== null
                ) {
                    setPassageEditorState(
                        getPassageEditorState(selectedQuestion.passage_id)
                    );
                }

                if (selectedQuestion.qtype === questionTypeConstants.MCQ.id) {
                    const answerStates = questionStates
                        .slice(1)
                        .map((state) => toEditorState(state))
                        .filter(Boolean);

                    const choices =
                        selectedQuestion.answer
                            ?.split(",")
                            .map((answer) => parseInt(answer, 10) > 0) || [];

                    setMcqAnswerStates(answerStates);
                    setSelectedChoices(choices);
                }

                if (selectedQuestion.qtype === questionTypeConstants.DESCRIPTIVE.id) {
                    const answerContent = await libraryService.getAnswerContent(
                        selectedQuestion
                    );

                    const answerStates = safeParseDraftArray(answerContent);
                    setDescriptiveAnswerState(toEditorState(answerStates[0]));
                }

                if (
                    selectedQuestion.qtype === questionTypeConstants.SUBQUESTION.id &&
                    selectedQuestion.is_subquestion === "true"
                ) {
                    const answerContent = await libraryService.getAnswerContent(
                        selectedQuestion
                    );

                    const answerStates = safeParseDraftArray(answerContent);
                    setDescriptiveAnswerState(toEditorState(answerStates[0]));
                }

                if (
                    selectedQuestion.qtype === questionTypeConstants.SUBQUESTION.id &&
                    selectedQuestion.is_subquestion === null
                ) {
                    setSubQuestionLoading(true);

                    const subIds =
                        selectedQuestion.sub_questions
                            ?.split(",")
                            .map((item) => item.trim())
                            .filter(Boolean) || [];

                  const subResults = await Promise.all(
                    subIds.map(async (subId) => {
                      const subResponse = await createService.getQuestionById(parseInt(subId, 10));
                      const subQuestion = normalizeQuestionResponse(subResponse);

                      if (!subQuestion) return null;

                      const [subQuestionContent, subAnswerContent] = await Promise.all([
                        libraryService.getQuestionContent(subQuestion),
                        libraryService.getAnswerContent(subQuestion),
                      ]);

                      const subQuestionStates = safeParseDraftArray(subQuestionContent);
                      const subAnswerParsedStates = safeParseDraftArray(subAnswerContent);

                      return {
                        questionState: toEditorState(subQuestionStates[0]),
                        answerState: toEditorState(subAnswerParsedStates[0]),
                      };
                    })
                  );

                  setSubQuestionContentStates(
                    subResults.map((item) => item?.questionState).filter(Boolean)
                  );

                  setSubQuestionAnswerStates(
                    subResults.map((item) => item?.answerState).filter(Boolean)
                  );
                    setSubQuestionLoading(false);
                }

                if (selectedQuestion.qtype === questionTypeConstants.SUBMISSION.id) {
                    const answerContent = await libraryService.getAnswerContent(
                        selectedQuestion
                    );

                    const attachmentNames =
                        answerContent
                            ?.replace(/^\[(.+)\]$/, "$1")
                            .replace(/"\"$/, "$1")
                            .split(",")
                            .map((item) => item.replace(/"/g, "").trim())
                            .filter((item) => item && item !== "[]") || [];

                    setAttachments(attachmentNames);
                }
            } catch (err) {
                setError(err.message || "Failed to load question.");
            } finally {
                setLoading(false);
                setSubQuestionLoading(false);
            }
        },
        [resetViewState, getPassageEditorState]
    );

    const detailRows = useMemo(() => {
        if (!question) return [];

    return [
      {
        label: "Category",
        value: meta.getCategoryName(question.category_id),
        show: Boolean(question.category_id),
      },
      {
        label: "Type",
        value: getQuestionTypeName(question.qtype, question.is_subquestion),
        show: Boolean(question.qtype),
      },
      {
        label: "Language",
        value: meta.getLanguageName(question.language_id),
        show: Boolean(question.language_id),
      },
      {
        label: "Difficulty",
        value: meta.getDifficultyName(question.difficulty_id),
        show: Boolean(question.difficulty_id),
      },
      {
        label: gradeText,
        value: meta.getGradeName(question.grade_id),
        show: Boolean(question.grade_id),
      },
      {
        label: "Subject",
        value: meta.getSubjectName(question.subject_id),
        show: Boolean(question.subject_id),
      },
      {
        label: "Year",
        value: meta.getYearName(question.year_id),
        show:
          customer !== customerConstants.EXO_CUSTOMER2 &&
          Boolean(question.year_id),
      },
      {
        label: "Term/Semester",
        value: meta.getTermName(question.term_id),
        show: Boolean(question.term_id),
      },
      {
        label: "Chapter/Module/Unit",
        value: meta.getChapterName(question.chapter_id),
        show: Boolean(question.chapter_id),
      },
      {
        label: "Source",
        value: meta.getSourceName(question.source_id),
        show: Boolean(question.source_id),
      },
      {
        label: "Author",
        value: meta.getAuthorName(question.author_id),
        show: Boolean(question.author_id),
      },
      {
        label: "Created By",
        value: [question.first_name, question.last_name]
          .filter(Boolean)
          .join(" "),
        show: Boolean(question.first_name && question.last_name),
      },
      {
        label: "Created At",
        value: getCreatedAtLocal(question.created_at),
        show: Boolean(question.created_at),
      },
    ].filter((item) => item.show);
  }, [question, meta, getQuestionTypeName, getCreatedAtLocal]);

  const value = useMemo(
    () => ({
      question,

      questionEditorState,
      passageEditorState,
      descriptiveAnswerState,

      mcqAnswerStates,
      selectedChoices,

      attachments,

      subQuestionContentStates,
      subQuestionAnswerStates,

      loading,
      subQuestionLoading,
      error,

      detailRows,
      loadQuestionForView,
    }),
    [
      question,
      questionEditorState,
      passageEditorState,
      descriptiveAnswerState,
      mcqAnswerStates,
      selectedChoices,
      attachments,
      subQuestionContentStates,
      subQuestionAnswerStates,
      loading,
      subQuestionLoading,
      error,
      detailRows,
      loadQuestionForView,
    ]
  );

  return (
    <ViewQuestionContext.Provider value={value}>
      {children}
    </ViewQuestionContext.Provider>
  );
}

export function useViewQuestion() {
  const context = useContext(ViewQuestionContext);

  if (!context) {
    throw new Error("useViewQuestion must be used inside ViewQuestionProvider");
  }

  return context;
}